import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { useLanguage } from '../context/LanguageContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const inputClass =
  'w-full px-4 py-3 border-2 border-neutral-200 rounded-xl focus:border-blue-400 focus:ring-0 transition-colors';

const ContactSection = () => {
  const { t } = useLanguage();

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    location: '',
    interestedService: '',
    message: '',
    source: '',
  });
  const [helpWith, setHelpWith] = useState([]);
  const [consent, setConsent] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // Preselect a service when the visitor arrives from a specific service CTA.
  useEffect(() => {
    const handler = (e) => {
      const service = e?.detail?.service;
      if (service) setFormData((prev) => ({ ...prev, interestedService: service }));
    };
    window.addEventListener('soulboard:apply', handler);
    return () => window.removeEventListener('soulboard:apply', handler);
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const toggleHelpWith = (option) => {
    setHelpWith((prev) =>
      prev.includes(option) ? prev.filter((o) => o !== option) : [...prev, option]
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (helpWith.length === 0) {
      toast.error(t('helpWithError'));
      return;
    }
    if (!consent) {
      toast.error(t('consentErr'));
      return;
    }

    setIsSubmitting(true);
    try {
      const interestedLabel =
        (t('interestedOptions') || []).find((o) => o.value === formData.interestedService)?.label || '';

      const res = await fetch(`${API_URL}/api/application`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          location: formData.location,
          help_with: helpWith,
          interested_service: interestedLabel,
          message: formData.message,
          source: formData.source,
          consent,
        }),
      });

      if (!res.ok) throw new Error('Submission failed');
      await res.json();

      setSubmitted(true);
      setFormData({ name: '', email: '', location: '', interestedService: '', message: '', source: '' });
      setHelpWith([]);
      setConsent(false);
    } catch (err) {
      toast.error(t('applyError'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-24 bg-[#faf7ee]/40">
      <div className="container mx-auto px-6">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-5xl md:text-6xl mb-8 text-center">{t('formHeading')}</h2>

          {submitted ? (
            <div
              className="bg-white border-2 border-neutral-200 rounded-3xl p-8 md:p-16 text-center max-w-2xl mx-auto"
              data-testid="application-success"
            >
              <h3
                className="text-neutral-900 mb-6"
                style={{ fontWeight: 400, letterSpacing: '0.005em', fontSize: 'clamp(2rem, 4vw, 3rem)' }}
              >
                {t('successTitle')}
              </h3>
              <div className="space-y-4 text-neutral-600 leading-relaxed mb-10">
                <p className="text-lg text-neutral-800">{t('successBody1')}</p>
                <p>{t('successBody2')}</p>
                <p>{t('successBody3')}</p>
              </div>
              <button
                type="button"
                onClick={() => {
                  setSubmitted(false);
                  document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
                }}
                data-testid="application-back-btn"
                className="px-9 py-4 text-sm uppercase tracking-[0.2em] font-bold text-neutral-900 hover:scale-[1.02] transition-transform duration-300"
                style={{ background: '#CCFF00' }}
              >
                {t('successBack')}
              </button>
            </div>
          ) : (
            <>
              <p className="text-xl text-neutral-600 text-center mb-16 max-w-2xl mx-auto leading-relaxed">
                {t('formIntro')}
              </p>

              <form
                onSubmit={handleSubmit}
                className="bg-white border-2 border-neutral-200 rounded-3xl p-8 md:p-12 space-y-8"
                data-testid="application-form"
              >
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-2">
                      {t('fieldNameLabel')} *
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      data-testid="application-name-input"
                      className={inputClass}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-2">
                      {t('fieldEmailLabel')} *
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      data-testid="application-email-input"
                      className={inputClass}
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-2">
                    {t('fieldLocationLabel')} *
                  </label>
                  <input
                    type="text"
                    name="location"
                    value={formData.location}
                    onChange={handleInputChange}
                    required
                    data-testid="application-location-input"
                    className={inputClass}
                    placeholder={t('fieldLocationPlaceholder')}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-3">
                    {t('helpWithLabel')} *
                  </label>
                  <div className="space-y-3" data-testid="application-helpwith-group">
                    {(t('helpWithOptions') || []).map((option, i) => (
                      <label key={i} className="flex items-start gap-3 cursor-pointer group">
                        <input
                          type="checkbox"
                          checked={helpWith.includes(option)}
                          onChange={() => toggleHelpWith(option)}
                          data-testid={`application-helpwith-${i}`}
                          className="mt-1 h-4 w-4 flex-shrink-0 accent-[#171717] cursor-pointer"
                        />
                        <span className="text-sm text-neutral-700 leading-relaxed">{option}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-2">
                    {t('interestedLabel')}
                  </label>
                  <select
                    name="interestedService"
                    value={formData.interestedService}
                    onChange={handleInputChange}
                    data-testid="application-interested-select"
                    className={inputClass}
                  >
                    <option value="">{t('interestedPlaceholder')}</option>
                    {(t('interestedOptions') || []).map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-2">
                    {t('messageLabel')} *
                  </label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    required
                    rows={5}
                    data-testid="application-message-textarea"
                    className={`${inputClass} resize-none`}
                    placeholder={t('messagePlaceholder')}
                  />
                  <p className="text-sm text-neutral-500 mt-2">{t('messageHelper')}</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-2">
                    {t('sourceLabel')}
                  </label>
                  <select
                    name="source"
                    value={formData.source}
                    onChange={handleInputChange}
                    data-testid="application-source-select"
                    className={inputClass}
                  >
                    <option value="">{t('sourcePlaceholder')}</option>
                    {(t('sourceOptions') || []).map((o, i) => (
                      <option key={i} value={o}>
                        {o}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="pt-2 border-t border-neutral-200">
                  <label className="flex items-start gap-3 cursor-pointer group">
                    <input
                      type="checkbox"
                      required
                      checked={consent}
                      onChange={(e) => setConsent(e.target.checked)}
                      data-testid="application-consent-checkbox"
                      className="mt-1 h-4 w-4 flex-shrink-0 accent-[#171717] cursor-pointer"
                    />
                    <span className="text-sm text-neutral-700 leading-relaxed">
                      {t('consentPre')}
                      <a
                        href="/terms"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="underline underline-offset-4 hover:text-neutral-900"
                        data-testid="application-privacy-link"
                      >
                        {t('consentLink')}
                      </a>
                      {t('consentPost')}{' '}
                      <span className="text-neutral-400" aria-hidden="true">*</span>
                    </span>
                  </label>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting || !consent}
                  data-testid="application-submit-btn"
                  className="w-full py-4 text-sm uppercase tracking-[0.25em] font-bold text-neutral-900 hover:shadow-xl hover:scale-[1.02] transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
                  style={{ background: '#CCFF00' }}
                >
                  {isSubmitting ? t('ctaSending') : t('ctaSend')}
                </button>

                <p className="text-sm text-neutral-500 text-center">{t('ctaNote')}</p>
              </form>
            </>
          )}
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
